import {
  sedrApi,
  type SedrGuestInvite,
  type SedrMember,
  type SedrReportRecipient,
  type SedrReportSchedule,
} from '../../api/sedr';
import { applyI18n, i18next, t } from '../../i18n';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { escapeHtml } from './sedrEscape';
import { sedrErrorMessageKey } from './sedrErrors';
import { showSedrReportAddRecipientDialog } from './sedrReportAddRecipientDialog';
import { classifyReportRecipient, type SedrReportRecipientKind } from './sedrReportRecipients';

function tRecipientKind(kind: SedrReportRecipientKind): string {
  return t(`sedr.scheduledReport.type.${kind}`);
}

function tRecipientStatus(optedOut: boolean): string {
  return optedOut
    ? t('sedr.scheduledReport.statusOptedOut')
    : t('sedr.scheduledReport.statusActive');
}

export async function renderSedrReportSchedulePanel(
  host: HTMLElement,
  options: {
    roomId: string;
    members: SedrMember[];
    guests: SedrGuestInvite[];
    readOnly: boolean;
  },
): Promise<void> {
  const { roomId, readOnly } = options;
  host.replaceChildren();
  host.className = 'sedr-tab-panel sedr-report-schedule';
  host.innerHTML = `<p class="muted" data-i18n="status.loading"></p>`;
  applyI18n(host);

  let schedule: SedrReportSchedule;
  let recipients: SedrReportRecipient[];
  try {
    [schedule, recipients] = await Promise.all([
      sedrApi.getReportSchedule(roomId),
      sedrApi.listReportRecipients(roomId),
    ]);
  } catch (cause) {
    host.innerHTML = `<p class="error-text">${t(sedrErrorMessageKey(cause))}</p>`;
    return;
  }

  const section = document.createElement('section');
  section.className = 'sedr-report-schedule__panel';
  section.innerHTML = `
    <h3 data-i18n="sedr.scheduledReport.title"></h3>
    <p class="muted" data-i18n="sedr.scheduledReport.subtitle"></p>
    <p class="error-text" id="sedr-report-error" hidden></p>
    <p class="muted" id="sedr-report-info" hidden></p>
    <form class="form-grid sedr-report-schedule__form" id="sedr-report-form">
      <label class="choice-row">
        <input type="checkbox" name="enabled" id="sedr-report-enabled" ${schedule.enabled ? 'checked' : ''} ${readOnly ? 'disabled' : ''} />
        <span data-i18n="sedr.scheduledReport.enabled"></span>
      </label>
      <label>
        <span data-i18n="sedr.scheduledReport.cadence"></span>
        <select name="cadence" id="sedr-report-cadence" ${readOnly ? 'disabled' : ''}>
          <option value="Weekly" data-i18n="sedr.scheduledReport.cadenceWeekly"></option>
          <option value="Monthly" data-i18n="sedr.scheduledReport.cadenceMonthly"></option>
          <option value="Yearly" data-i18n="sedr.scheduledReport.cadenceYearly"></option>
        </select>
      </label>
      <label id="sedr-report-weekday-wrap">
        <span data-i18n="sedr.scheduledReport.weekday"></span>
        <select name="weekday" id="sedr-report-weekday" ${readOnly ? 'disabled' : ''}>
          <option value="1" data-i18n="sedr.scheduledReport.weekdayMon"></option>
          <option value="2" data-i18n="sedr.scheduledReport.weekdayTue"></option>
          <option value="3" data-i18n="sedr.scheduledReport.weekdayWed"></option>
          <option value="4" data-i18n="sedr.scheduledReport.weekdayThu"></option>
          <option value="5" data-i18n="sedr.scheduledReport.weekdayFri"></option>
          <option value="6" data-i18n="sedr.scheduledReport.weekdaySat"></option>
          <option value="0" data-i18n="sedr.scheduledReport.weekdaySun"></option>
        </select>
      </label>
      <label id="sedr-report-day-wrap">
        <span data-i18n="sedr.scheduledReport.dayOfMonth"></span>
        <input type="number" name="dayOfMonth" id="sedr-report-day" min="1" max="28" ${readOnly ? 'disabled' : ''} />
      </label>
      <label id="sedr-report-month-wrap">
        <span data-i18n="sedr.scheduledReport.month"></span>
        <input type="number" name="month" id="sedr-report-month" min="1" max="12" ${readOnly ? 'disabled' : ''} />
      </label>
      <label>
        <span data-i18n="sedr.scheduledReport.sendTimeUtc"></span>
        <input type="time" name="sendTimeUtc" id="sedr-report-time" required ${readOnly ? 'disabled' : ''} />
      </label>
      <label>
        <span data-i18n="sedr.scheduledReport.pdfFormat"></span>
        <select name="pdfFormat" id="sedr-report-pdf-format" ${readOnly ? 'disabled' : ''}>
          <option value="Summary" data-i18n="sedr.scheduledReport.pdfFormatSummary"></option>
          <option value="Detailed" data-i18n="sedr.scheduledReport.pdfFormatDetailed"></option>
        </select>
      </label>
      <label class="choice-row">
        <input type="checkbox" name="includeObsolete" id="sedr-report-include-obsolete" ${schedule.includeObsolete ? 'checked' : ''} ${readOnly ? 'disabled' : ''} />
        <span data-i18n="sedr.scheduledReport.includeObsolete"></span>
      </label>
      <p class="muted" id="sedr-report-next"></p>
      ${readOnly ? '' : `
        <div class="sedr-report-schedule__actions">
          <button type="submit" class="button primary" data-i18n="sedr.scheduledReport.save"></button>
          <button type="button" class="button ghost" id="sedr-report-send-now" data-i18n="sedr.scheduledReport.sendNow"></button>
        </div>
      `}
    </form>
    <div class="sedr-report-schedule__recipients">
      <div class="sedr-report-schedule__recipients-header">
        <div>
          <h4 data-i18n="sedr.scheduledReport.recipients"></h4>
          <p class="muted" data-i18n="sedr.scheduledReport.recipientsHint"></p>
        </div>
        ${readOnly ? '' : `
          <button type="button" class="button primary" id="sedr-report-add" data-i18n="sedr.scheduledReport.addRecipient"></button>
        `}
      </div>
      <div class="files-table-wrap">
        <table class="data-table sedr-report-schedule__table">
          <thead>
            <tr>
              <th data-i18n="sedr.scheduledReport.colName"></th>
              <th data-i18n="sedr.scheduledReport.colEmail"></th>
              <th data-i18n="sedr.scheduledReport.colType"></th>
              <th data-i18n="sedr.scheduledReport.colStatus"></th>
              ${readOnly ? '' : `<th class="data-table__actions-heading" data-i18n="sedr.scheduledReport.colActions"></th>`}
            </tr>
          </thead>
          <tbody id="sedr-report-recipient-body"></tbody>
        </table>
      </div>
    </div>
  `;

  host.replaceChildren(section);
  applyI18n(section);

  const errorEl = section.querySelector('#sedr-report-error') as HTMLParagraphElement;
  const infoEl = section.querySelector('#sedr-report-info') as HTMLParagraphElement;
  const form = section.querySelector('#sedr-report-form') as HTMLFormElement;
  const cadenceEl = section.querySelector('#sedr-report-cadence') as HTMLSelectElement;
  const weekdayEl = section.querySelector('#sedr-report-weekday') as HTMLSelectElement;
  const dayEl = section.querySelector('#sedr-report-day') as HTMLInputElement;
  const monthEl = section.querySelector('#sedr-report-month') as HTMLInputElement;
  const timeEl = section.querySelector('#sedr-report-time') as HTMLInputElement;
  const pdfFormatEl = section.querySelector('#sedr-report-pdf-format') as HTMLSelectElement;
  const includeObsoleteEl = section.querySelector('#sedr-report-include-obsolete') as HTMLInputElement;
  const nextEl = section.querySelector('#sedr-report-next') as HTMLParagraphElement;
  const bodyEl = section.querySelector('#sedr-report-recipient-body') as HTMLTableSectionElement;

  cadenceEl.value = schedule.cadence;
  weekdayEl.value = String(schedule.weekday ?? 1);
  dayEl.value = String(schedule.dayOfMonth ?? 1);
  monthEl.value = String(schedule.month ?? 1);
  timeEl.value = schedule.sendTimeUtc.length === 5 ? schedule.sendTimeUtc : '09:00';
  pdfFormatEl.value = schedule.pdfFormat === 'Detailed' ? 'Detailed' : 'Summary';
  includeObsoleteEl.checked = Boolean(schedule.includeObsolete);

  const showError = (message: string) => {
    errorEl.hidden = false;
    errorEl.textContent = message;
    infoEl.hidden = true;
  };
  const showInfo = (message: string) => {
    infoEl.hidden = false;
    infoEl.textContent = message;
    errorEl.hidden = true;
  };
  const clearMessages = () => {
    errorEl.hidden = true;
    infoEl.hidden = true;
  };

  const syncCadenceFields = () => {
    const cadence = cadenceEl.value;
    (section.querySelector('#sedr-report-weekday-wrap') as HTMLElement).hidden = cadence !== 'Weekly';
    (section.querySelector('#sedr-report-day-wrap') as HTMLElement).hidden =
      cadence !== 'Monthly' && cadence !== 'Yearly';
    (section.querySelector('#sedr-report-month-wrap') as HTMLElement).hidden = cadence !== 'Yearly';
  };
  syncCadenceFields();
  cadenceEl.addEventListener('change', syncCadenceFields);

  const updateNextLabel = () => {
    if (!schedule.enabled || !schedule.nextSendAt) {
      nextEl.textContent = t('sedr.scheduledReport.nextNone');
      return;
    }
    nextEl.textContent = t('sedr.scheduledReport.nextAt', {
      when: new Date(schedule.nextSendAt).toISOString().replace('T', ' ').slice(0, 16) + ' UTC',
    });
  };
  updateNextLabel();

  const renderRecipients = () => {
    bodyEl.replaceChildren();
    if (recipients.length === 0) {
      const tr = document.createElement('tr');
      const colSpan = readOnly ? 4 : 5;
      tr.innerHTML = `<td colspan="${colSpan}" class="muted" data-i18n="sedr.scheduledReport.recipientsEmpty"></td>`;
      bodyEl.append(tr);
      applyI18n(tr);
      return;
    }
    for (const recipient of recipients) {
      const kind = classifyReportRecipient(recipient);
      const tr = document.createElement('tr');
      if (recipient.optedOut) {
        tr.classList.add('sedr-report-schedule__row--opted-out');
      }
      tr.innerHTML = `
        <td></td>
        <td></td>
        <td>${escapeHtml(tRecipientKind(kind))}</td>
        <td><span class="status-badge ${recipient.optedOut ? 'status-badge--warning' : 'status-badge--success'}">${escapeHtml(tRecipientStatus(recipient.optedOut))}</span></td>
        ${readOnly ? '' : `<td><button type="button" class="button ghost danger" data-i18n="sedr.scheduledReport.removeRecipient"></button></td>`}
      `;
      const cells = tr.querySelectorAll('td');
      cells[0]!.textContent = recipient.displayName;
      cells[1]!.textContent = recipient.email;
      const removeBtn = tr.querySelector('button');
      removeBtn?.addEventListener('click', () => {
        void (async () => {
          clearMessages();
          try {
            await sedrApi.deleteReportRecipient(roomId, recipient.id);
            recipients = recipients.filter((r) => r.id !== recipient.id);
            renderRecipients();
          } catch (cause) {
            showError(t(sedrErrorMessageKey(cause)));
          }
        })();
      });
      bodyEl.append(tr);
      applyI18n(tr);
    }
    const scheduleTable = section.querySelector('table.data-table') as HTMLTableElement | null;
    if (scheduleTable) {
      enhanceDataTableCards(scheduleTable);
    }
  };
  renderRecipients();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (readOnly) return;
    void (async () => {
      clearMessages();
      try {
        schedule = await sedrApi.putReportSchedule(roomId, {
          enabled: (section.querySelector('#sedr-report-enabled') as HTMLInputElement).checked,
          cadence: cadenceEl.value,
          weekday: Number(weekdayEl.value),
          dayOfMonth: Number(dayEl.value),
          month: Number(monthEl.value),
          sendTimeUtc: timeEl.value,
          locale: i18next.language?.slice(0, 2) || 'en',
          pdfFormat: pdfFormatEl.value,
          includeObsolete: includeObsoleteEl.checked,
        });
        updateNextLabel();
        showInfo(t('sedr.scheduledReport.saved'));
      } catch (cause) {
        showError(t(sedrErrorMessageKey(cause)));
      }
    })();
  });

  section.querySelector('#sedr-report-send-now')?.addEventListener('click', () => {
    void (async () => {
      clearMessages();
      try {
        const result = await sedrApi.sendReportNow(roomId);
        showInfo(t('sedr.scheduledReport.sent', { sent: String(result.sentCount), total: String(result.recipientCount) }));
      } catch (cause) {
        showError(t(sedrErrorMessageKey(cause)));
      }
    })();
  });

  section.querySelector('#sedr-report-add')?.addEventListener('click', () => {
    clearMessages();
    showSedrReportAddRecipientDialog({
      roomId,
      members: options.members,
      guests: options.guests,
      onAdded: (created) => {
        recipients = [...recipients, created];
        renderRecipients();
      },
    });
  });
}
